import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { getToken } from '../../whatsapp/function/token';
import domain from '../../reusecomponent/domain';
import { FaArrowLeft, FaPaperclip, FaTrash, FaSpinner, FaCloudUploadAlt } from 'react-icons/fa';

export default function RaiseTicket() {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Helper to count words in the message
  const countWords = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
  };

  const wordCount = countWords(message);
  const isWordCountValid = wordCount >= 50;

  // File validation and handling
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const addFiles = (selectedFiles) => {
    const validFiles = [];
    for (let file of selectedFiles) {
      // Validate file size (10MB = 10 * 1024 * 1024 bytes)
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire({
          title: 'File Too Large',
          text: `File "${file.name}" exceeds the 10MB limit.`,
          icon: 'warning',
          confirmButtonColor: '#075E54'
        });
        continue;
      }

      // Check file types (Photos or PDFs)
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          title: 'Unsupported Format',
          text: `File "${file.name}" must be a PDF or an Image (JPEG, PNG, GIF).`,
          icon: 'warning',
          confirmButtonColor: '#075E54'
        });
        continue;
      }

      validFiles.push(file);
    }

    setFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!subject.trim()) {
      Swal.fire('Error', 'Subject is required.', 'error');
      return;
    }
    if (subject.length > 50) {
      Swal.fire('Error', 'Subject cannot exceed 50 characters.', 'error');
      return;
    }
    if (wordCount < 50) {
      Swal.fire('Error', 'The message must contain at least 50 words.', 'error');
      return;
    }

    setSubmitting(true);

    const formData = new FormData();
    formData.append('subject', subject);
    formData.append('message', message);

    files.forEach((file) => {
      formData.append('files[]', file);
    });

    try {
      const userToken = getToken();
      const response = await axios.post(`${domain()}/tickets`, formData, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Accept': 'application/json',
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data && response.data.status) {
        Swal.fire({
          title: 'Ticket Raised!',
          text: response.data.message || 'Support ticket raised successfully.',
          icon: 'success',
          confirmButtonColor: '#075E54'
        }).then(() => {
          navigate('/client-area/whatsapp/tickets');
        });
      } else {
        Swal.fire('Error', response.data.message || 'Failed to raise support ticket.', 'error');
      }
    } catch (error) {
      console.error('Error raising ticket:', error);
      const errMsg = error.response?.data?.message || 'Something went wrong. Please check your inputs and try again.';
      Swal.fire({
        title: 'Error',
        text: errMsg,
        icon: 'error',
        confirmButtonColor: '#075E54'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Drag and Drop support
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  return (
    <div className="col-lg-12 grid-margin stretch-card p-0 px-sm-3">
      {/* Main card */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', backgroundColor: '#ffffff' }}>
        <div className="card-body">
          
          {/* Back button */}
          <button 
            className="btn btn-link text-decoration-none d-inline-flex align-items-center gap-2 text-secondary mb-3 p-0"
            onClick={() => navigate('/client-area/whatsapp/tickets')}
            style={{ fontSize: '14px' }}
          >
            <FaArrowLeft /> Back to Tickets
          </button>
          
          <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
            <div>
              <h5 className="mb-0 fw-bold" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1.25rem' }}>Raise Support Ticket</h5>
              <p className="text-secondary mb-0 small">Briefly describe the query or problem you are encountering.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            
            {/* Subject Field */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label fw-bold text-dark mb-0" style={{ fontSize: '14px' }}>Subject</label>
                <span className={`small ${subject.length > 50 ? 'text-danger' : 'text-muted'}`}>
                  {subject.length} / 50 characters
                </span>
              </div>
              <input
                type="text"
                className="form-control text-dark border"
                placeholder="Brief summary of your query or issue..."
                value={subject}
                onChange={(e) => setSubject(e.target.value.slice(0, 100))}
                required
                disabled={submitting}
                style={{ 
                  borderRadius: '8px', 
                  border: '1px solid #cbd5e1', 
                  padding: '10px 14px', 
                  boxShadow: 'none',
                  fontSize: '14px',
                  backgroundColor: '#ffffff'
                }}
              />
              {subject.length > 50 && (
                <div className="form-text text-danger mt-1">Subject cannot exceed 50 characters.</div>
              )}
            </div>

            {/* Message Field */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label fw-bold text-dark mb-0" style={{ fontSize: '14px' }}>Detailed Description</label>
                <span className={`badge px-2 py-1 rounded-pill ${isWordCountValid ? 'bg-success bg-opacity-10 text-success' : 'bg-warning bg-opacity-10 text-warning'}`}>
                  {wordCount} / 50 words minimum
                </span>
              </div>
              <textarea
                className="form-control text-dark border"
                rows="6"
                placeholder="Please describe your issue in detail. Make sure to provide at least 50 words to explain the problem clearly..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                disabled={submitting}
                style={{ 
                  borderRadius: '8px', 
                  border: '1px solid #cbd5e1', 
                  padding: '10px 14px', 
                  boxShadow: 'none',
                  fontSize: '14px',
                  backgroundColor: '#ffffff'
                }}
              ></textarea>
              {!isWordCountValid && message.length > 0 && (
                <div className="form-text text-warning mt-1">
                  You need {50 - wordCount} more word(s) to reach the minimum limit.
                </div>
              )}
            </div>

            {/* File Upload Zone */}
            <div className="mb-4">
              <label className="form-label fw-bold text-dark mb-2" style={{ fontSize: '14px' }}>Attachments (Optional)</label>
              
              <div 
                className="p-4 text-center cursor-pointer transition-all mb-3 position-relative"
                style={{ 
                  borderRadius: '12px',
                  border: '2px dashed #cbd5e1',
                  backgroundColor: '#f8fafc',
                  cursor: submitting ? 'not-allowed' : 'pointer'
                }}
                onDragOver={handleDragOver}
                onDrop={submitting ? undefined : handleDrop}
                onClick={() => !submitting && fileInputRef.current.click()}
              >
                <input
                  type="file"
                  multiple
                  className="d-none"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/jpeg,image/png,image/gif,application/pdf"
                  disabled={submitting}
                />
                <FaCloudUploadAlt size={32} className="text-secondary opacity-60 mb-2" />
                <p className="fw-semibold text-dark mb-1">Drag & Drop files here or click to browse</p>
                <p className="text-muted small mb-0">Supported formats: JPG, PNG, GIF, PDF (Max 10MB per file)</p>
              </div>

              {/* Uploaded Files List */}
              {files.length > 0 && (
                <div className="d-flex flex-column gap-2 mt-3">
                  <span className="small text-secondary fw-bold">Selected Files ({files.length}):</span>
                  {files.map((file, idx) => (
                    <div 
                      key={idx}
                      className="d-flex align-items-center justify-content-between p-2 rounded-3 border bg-white"
                      style={{ borderRadius: '8px' }}
                    >
                      <div className="d-flex align-items-center gap-2 overflow-hidden">
                        <FaPaperclip className="text-secondary flex-shrink-0" />
                        <span className="text-dark small text-truncate fw-medium" style={{ maxWidth: '350px' }}>
                          {file.name}
                        </span>
                        <span className="text-muted small flex-shrink-0">
                          ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-link text-danger p-0 border-0 shadow-none"
                        onClick={() => removeFile(idx)}
                        disabled={submitting}
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
              <button
                type="button"
                className="btn btn-light px-4 py-2 fw-semibold"
                style={{ borderRadius: '8px', border: '1px solid #cbd5e1', padding: '8px 20px', fontSize: '13.5px' }}
                onClick={() => navigate('/client-area/whatsapp/tickets')}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn text-white px-5 py-2 fw-semibold shadow-sm"
                style={{ 
                  backgroundColor: '#075E54', 
                  borderColor: '#075E54',
                  borderRadius: '8px',
                  padding: '8px 24px',
                  fontSize: '13.5px',
                  opacity: submitting || !isWordCountValid || subject.length > 50 || !subject.trim() ? 0.65 : 1
                }}
                disabled={submitting || !isWordCountValid || subject.length > 50 || !subject.trim()}
              >
                {submitting ? (
                  <>
                    <FaSpinner className="spinner-border border-0 text-white p-0 m-0" style={{ width: '1.2rem', height: '1.2rem' }} />
                    Submitting...
                  </>
                ) : (
                  'Submit Ticket'
                )}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}
